import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Button, Grid, Menu, MenuItem, TextField, Paper, Typography, InputAdornment, CircularProgress, IconButton } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ClearIcon from "@material-ui/icons/Clear";
import ConfirmationModal from "../../modal/ConfirmationModal";
import ResponsiveTable from "../../table/ResponsiveTable";
import VenueActModal from "./VenueActModal";
import AgentActInvitationModal from "./AgentActInvitationModal";
import TableRowProfile from "../../util/TableRowProfile";
import image from "../../../helper/image";
import AgentVenueContactModal from "./AgentVenueContactModal";
import AgentVenueUpdateIncludeSuper from "./AgentVenueUpdateIncludeSuper";
import EditAgentModal from "../../venue/AgentTable/EditAgentModal";
import Loading from "../../loadingSpinner/Loading";
import VenuePaginationToolbar from "./VenuePaginationToolbar";
import "./AgentVenueTable.css";

@inject("agentVenueStore", "templateStore", "venueActStore", "profileStore")
@observer
class AgentVenueTable extends Component {
    state = {
        deleteModalState: false,
        venueActModalState: false,
        actInvitationModalState: false,
        agentVenue: undefined,
        agentVenueContact: undefined,
        venueName: "this venue",
        actionAnchor: [],
        openAgentVenueContactModal: false,
        openUpdateAgentVenueIncludeSuper: false,
        editBookingDaysOpen: false,
        localSearchValue: "",
        lastKnownStoreTerm: ""
    };

    columns = [
        { key: "venue", label: "Venue", width: "25%", sortable: true, isString: true, addComp: true },
        { key: "bookingDay", label: "Booking Days", width: "15%" },
        { key: "contact", label: "Venue Manager", width: "15%" },
        { key: "emailPhone", label: "Email & Phone", width: "25%" },
        { key: "action", label: "Actions", width: "10%" }
    ];

    debounceTimer = null;

    componentDidUpdate() {
        const currentStoreTerm = this.props.agentVenueStore.venueRosterSearchTerm;
        const { lastKnownStoreTerm, localSearchValue } = this.state;

        if (currentStoreTerm !== lastKnownStoreTerm && currentStoreTerm !== localSearchValue) {
            this.setState({
                localSearchValue: currentStoreTerm,
                lastKnownStoreTerm: currentStoreTerm
            });
        }
    }

    componentWillUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ localSearchValue: value });

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.debounceTimer = setTimeout(() => {
            const { agentVenueStore, profileStore } = this.props;
            agentVenueStore.setVenueRosterSearchTerm(value);
            this.setState({ lastKnownStoreTerm: value });

            const currentProfile = profileStore.getCurrentProfile();
            const isConsultant = currentProfile?.isConsultant;
            if (value && value.trim()) {
                agentVenueStore.searchVenueRoster(currentProfile?.id, isConsultant);
            } else {
                agentVenueStore.clearVenueRosterSearch();
                agentVenueStore.resetAgentVenues();
                const apiCall = isConsultant
                    ? agentVenueStore.getConsultantVenueRosterV2(currentProfile?.id, 30, 1)
                    : agentVenueStore.getMyVenueRosterV2(currentProfile?.id, 30, 1);
                apiCall.then(() => {
                    agentVenueStore.setAgentVenuePage(1);
                });
            }
        }, 500);
    };

    handleClearSearch = () => {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.setState({ localSearchValue: "", lastKnownStoreTerm: "" });
        const { agentVenueStore, profileStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        const isConsultant = currentProfile?.isConsultant;
        agentVenueStore.clearVenueRosterSearch();
        agentVenueStore.resetAgentVenues();
        const apiCall = isConsultant
            ? agentVenueStore.getConsultantVenueRosterV2(currentProfile?.id, 30, 1)
            : agentVenueStore.getMyVenueRosterV2(currentProfile?.id, 30, 1);
        apiCall.then(() => {
            agentVenueStore.setAgentVenuePage(1);
        });
    };

    getFilteredVenues = () => {
        const { agentVenues, agentVenuePage, isMoreAgentVenues, venuePaginationState, highestLoadedPage, venueRosterSearchTerm } = this.props.agentVenueStore;
        const pageSize = 30;
        const hasSearch = venueRosterSearchTerm.length > 0;

        if (agentVenues.length === 0) {
            return [];
        }

        if (hasSearch) {
            return agentVenues;
        }

        const allLoaded = !isMoreAgentVenues && !venuePaginationState.isLoadingAll && agentVenues.length > 0;
        if (allLoaded) {
            return agentVenues;
        }

        if (agentVenuePage <= highestLoadedPage && agentVenues.length > 0) {
            const startIndex = (agentVenuePage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            if (startIndex < agentVenues.length) {
                return agentVenues.slice(startIndex, endIndex);
            }
        }

        return agentVenues;
    };

    handleOpenAgentVenueIncludeSuperDefault = (data) => {
        this.setState({
            openUpdateAgentVenueIncludeSuper: true,
            agentVenue: data
        });
    };

    handleCloseAgentVenueIncludeSuperDefault = () => {
        this.setState({
            openUpdateAgentVenueIncludeSuper: false,
            agentVenue: undefined
        });
    };

    handleSaveAgentVenueIncludeSuperDefault = (value) => {
        const cachedData = localStorage.getItem("agentVenueRoster");
        if (cachedData) {
            var agentVenues = JSON.parse(cachedData);
            agentVenues.map(agentVenue => {
                if (agentVenue.id === this.state.agentVenue?.id) {
                    agentVenue.defaultIncludeSuper = value;
                }
            });
            localStorage.setItem("agentVenueRoster", JSON.stringify(agentVenues));
        }
        this.setState({
            openUpdateAgentVenueIncludeSuper: false,
            agentVenue: undefined
        });
    };

    loadMoreAgentVenues = (page) => {
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const { agentVenueStore } = this.props;
        const isConsultant = currentProfile?.isConsultant;

        if (agentVenueStore.venueRosterSearchTerm.trim()) {
            agentVenueStore.searchVenueRosterNextPage(currentProfile?.id, page, isConsultant).then(() => {
                agentVenueStore.setAgentVenuePage(page);
            });
        } else {
            if (isConsultant) {
                agentVenueStore.getConsultantVenueRosterV2(currentProfile?.id, 30, page).then(() => {
                    this.props.templateStore.hideLoadingScreen();
                    agentVenueStore.setAgentVenuePage(page);
                });
            } else {
                agentVenueStore.getMyVenueRosterV2(currentProfile?.id, 30, page).then(() => {
                    this.props.templateStore.hideLoadingScreen();
                    agentVenueStore.setAgentVenuePage(page);
                });
            }
        }
    };

    handleShowMore = e => {
        var page = this.props.agentVenueStore.agentVenuePage + 1;
        if (!this.props.agentVenueStore.venueRosterSearchTerm.trim()) {
            this.props.templateStore.showLoadingScreen();
        }
        this.loadMoreAgentVenues(page);
    };

    mapBookingDay = data => {
        var bookingDay = "";
        if (data) {
            if (data.monday) bookingDay += "Mon, ";
            if (data.tuesday) bookingDay += "Tue, ";
            if (data.wednesday) bookingDay += "Wed, ";
            if (data.thursday) bookingDay += "Thu, ";
            if (data.friday) bookingDay += "Fri, ";
            if (data.saturday) bookingDay += "Sat, ";
            if (data.sunday) bookingDay += "Sun, ";
        }
        return bookingDay.substr(0, bookingDay.length - 2);
    };

    setActionAnchor = (element, agentVenueId) => {
        var tempAnchor = [];
        tempAnchor[agentVenueId] = element;
        this.setState({ actionAnchor: tempAnchor });
    };

    unsetActionAnchor = agentVenueId => {
        this.setState({ actionAnchor: [] });
    };

    openEditBookingDays = agentVenue => {
        this.props.agentVenueStore.setEditedAgentVenue(agentVenue);
        this.setState({ editBookingDaysOpen: true, actionAnchor: [] });
    };

    closeEditBookingDays = () => {
        this.props.agentVenueStore.setEditedAgentVenue(undefined);
        this.setState({ editBookingDaysOpen: false });
    };

    mapApprovedToRowData = () => {
        const rowData = [];
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const filteredVenues = this.getFilteredVenues();

        filteredVenues.forEach(data => {
            var row = {};
            var venue = data.venue;

            const customContact = data.agentVenueContact;
            const hasCustomContact = customContact && customContact.switchOn && !customContact.deleted;

            const effectiveContactName = hasCustomContact
                ? (customContact.manageContactName || customContact.venueContactName || data.contactName || data.manageContactName)
                : (data.contactName || data.manageContactName);
            const effectiveEmail = hasCustomContact
                ? (customContact.venueEmail || data.manageContactEmail)
                : data.manageContactEmail;
            const effectivePhone = hasCustomContact
                ? (customContact.venuePhone || data.manageContactPhone)
                : data.manageContactPhone;

            row.id = "a" + data.id;
            row.data = data;
            row.bookingDay = this.mapBookingDay(data);

            row.venue = venue && {
                newFormat: (
                    <TableRowProfile 
                        profileId={venue.id} 
                        name={venue.fullName} 
                        avatarUrl={image.getProfileImage(venue)} 
                    />
                ),
                value: venue.fullName
            };

            row.contact = (
                <div className="venue-contact-name">
                    {effectiveContactName || "-"}
                </div>
            );

            row.emailPhone = (
                <div className="venue-contact-chips">
                    {effectiveEmail && (
                        <a href={`mailto:${effectiveEmail}`} className="venue-contact-chip email">
                            <i className="fa fa-envelope"></i>
                            {effectiveEmail}
                        </a>
                    )}
                    {effectivePhone && (
                        <a href={`tel:${effectivePhone}`} className="venue-contact-chip phone">
                            <i className="fa fa-phone"></i>
                            {effectivePhone}
                        </a>
                    )}
                    {!effectiveEmail && !effectivePhone && (
                        <span className="venue-contact-chip empty">No contact info</span>
                    )}
                </div>
            );

            row.action = (
                <Fragment>
                    <div className="venue-actions-dropdown">
                        <Button 
                            className="venue-actions-dropdown-btn"
                            onClick={e => this.setActionAnchor(e.currentTarget, data.id)}
                        >
                            Actions <ArrowDropDownIcon />
                        </Button>
                        {data.id && (
                            <Menu
                                open={this.state.actionAnchor[data.id] != null}
                                onClose={() => this.unsetActionAnchor(data.id)}
                                anchorEl={this.state.actionAnchor[data.id]}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                                <MenuItem
                                    onClick={() => { this.unsetActionAnchor(); this.props.createBooking(venue?.id); }}
                                    className="venue-menu-item-primary"
                                >
                                    <i className="fa fa-plus venue-menu-icon"></i>
                                    Create Booking
                                </MenuItem>
                                <div className="venue-menu-divider" />
                                <MenuItem onClick={() => this.openEditBookingDays(data)}>
                                    <i className="fa fa-pencil venue-menu-icon"></i>
                                    Edit Booking Days
                                </MenuItem>
                                <MenuItem onClick={() => { this.unsetActionAnchor(); this.handleOpenAgentVenueIncludeSuperDefault(data); }}>
                                    <i className="fa fa-shield-halved venue-menu-icon"></i>
                                    Set Super Default
                                </MenuItem>
                                {data.manage && (
                                    <MenuItem onClick={() => { this.unsetActionAnchor(); this.props.redirectVenueFavourite(data); }}>
                                        <i className="fa fa-star venue-menu-icon"></i>
                                        Manage Favorite Acts
                                    </MenuItem>
                                )}
                                <MenuItem onClick={() => { this.unsetActionAnchor(); this.openAgentVenueModal(data); }}>
                                    <i className="fa fa-edit venue-menu-icon"></i>
                                    Update Contact Info
                                </MenuItem>
                                {data.agentId === currentProfile?.id && (
                                    <Fragment>
                                        <div className="venue-menu-divider" />
                                        <MenuItem 
                                            onClick={() => { this.unsetActionAnchor(); this.openDeleteModal(data); }}
                                            className="venue-menu-item-danger"
                                        >
                                            <i className="fa fa-trash venue-menu-icon"></i>
                                            Remove
                                        </MenuItem>
                                    </Fragment>
                                )}
                            </Menu>
                        )}
                    </div>
                </Fragment>
            );

            rowData.push(row);
        });

        return rowData;
    };

    openDeleteModal = agentVenue => {
        this.setState({
            deleteModalState: true,
            agentVenue: agentVenue,
            venueName: agentVenue.venue?.profileName || agentVenue.venue?.fullName
        });
    };

    closeDeleteModal = () => {
        this.setState({
            deleteModalState: false,
            agentVenue: undefined
        });
    };

    openAgentVenueModal = agentVenue => {
        this.setState({
            openAgentVenueContactModal: true,
            agentVenueContact: agentVenue
        });
    };

    closeAgentVenueModal = () => {
        this.setState({
            openAgentVenueContactModal: false,
            agentVenueContact: undefined
        });
    };

    handleVenueContactSave = (agentVenueId, contactData) => {
        this.props.agentVenueStore.updateVenueContactData(agentVenueId, contactData);
    };

    handleDelete = () => {
        this.props.agentVenueStore.deleteAgentVenue(this.state.agentVenue.id).then(() => {
            this.props.templateStore.openSnackbar("Venue relation has been deleted.");
            this.closeDeleteModal();
        });
    };

    openActModal = agentVenue => {
        this.setState({
            venueActModalState: true,
            agentVenue: agentVenue
        });
    };

    closeActModal = () => {
        this.setState({
            venueActModalState: false,
            agentVenue: undefined
        });
    };

    openAddActModal = () => {
        this.setState(
            {
                venueActModalState: false,
                actInvitationModalState: true
            },
            () => {
                this.props.venueActStore.clearInviteData();
            }
        );
    };

    closeAddActModal = () => {
        this.setState({
            actInvitationModalState: false,
            venueActModalState: true
        });
    };

    handleLoadPage = (page) => {
        const { agentVenueStore, profileStore, templateStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        const currentPage = agentVenueStore.agentVenuePage;
        const highestLoaded = agentVenueStore.highestLoadedPage;
        
        if (page === currentPage || agentVenueStore.isLoadingVenues) return;
        
        if (page > highestLoaded) {
            templateStore.showLoadingScreen();
            agentVenueStore.setIsLoadingVenues(true);
            
            const apiCall = currentProfile?.isConsultant 
                ? agentVenueStore.getConsultantVenueRosterV2(currentProfile?.id, 30, page)
                : agentVenueStore.getMyVenueRosterV2(currentProfile?.id, 30, page);
                
            apiCall.then(() => {
                agentVenueStore.setHighestLoadedPage(page);
                agentVenueStore.setAgentVenuePage(page);
            }).finally(() => {
                templateStore.hideLoadingScreen();
                agentVenueStore.setIsLoadingVenues(false);
            });
        } else {
            agentVenueStore.setAgentVenuePage(page);
        }
    };

    handleResetToPaginated = () => {
        const { agentVenueStore, profileStore, templateStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        
        agentVenueStore.resetAgentVenues();
        templateStore.showLoadingScreen();
        agentVenueStore.setIsLoadingVenues(true);
        
        const apiCall = currentProfile?.isConsultant 
            ? agentVenueStore.getConsultantVenueRosterV2(currentProfile?.id, 30, 1)
            : agentVenueStore.getMyVenueRosterV2(currentProfile?.id, 30, 1);
            
        apiCall.then(() => {
            agentVenueStore.setHighestLoadedPage(1);
        }).finally(() => {
            templateStore.hideLoadingScreen();
            agentVenueStore.setIsLoadingVenues(false);
        });
    };

    render() {
        const rowData = this.mapApprovedToRowData();
        const { isLoadingVenues, isSearchingVenueRoster, venueRosterSearchTerm } = this.props.agentVenueStore;
        const filteredVenues = this.getFilteredVenues();
        const hasSearch = venueRosterSearchTerm.length > 0;

        return (
            <Fragment>
                <Paper style={{ padding: 24, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    <div style={{ marginBottom: 16 }}>
                        <TextField
                            placeholder="Search venues..."
                            variant="outlined"
                            size="small"
                            value={this.state.localSearchValue}
                            onChange={this.handleSearchChange}
                            style={{ width: "100%", maxWidth: 400 }}
                            InputProps={{
                                style: { fontSize: 14 },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isSearchingVenueRoster ? (
                                            <CircularProgress size={18} style={{ color: "#FF0C9C" }} />
                                        ) : this.state.localSearchValue ? (
                                            <IconButton size="small" onClick={this.handleClearSearch}>
                                                <ClearIcon style={{ fontSize: 18 }} />
                                            </IconButton>
                                        ) : null}
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>

                    {hasSearch && !isSearchingVenueRoster && (
                        <div style={{ 
                            fontSize: 13, 
                            color: "#666",
                            marginBottom: 8,
                            padding: "4px 0"
                        }}>
                            <strong>{filteredVenues.length}</strong> venue{filteredVenues.length !== 1 ? 's' : ''} found for "<span style={{ color: "#FF0C9C", fontWeight: 500 }}>{venueRosterSearchTerm}</span>"
                        </div>
                    )}

                    <div style={{ position: "relative" }}>
                        <Loading showed={isLoadingVenues} />
                        {rowData.length > 0 ? (
                            <ResponsiveTable 
                                title="Active Venues" 
                                sortable={true} 
                                showToolbar={true} 
                                columns={this.columns} 
                                rows={rowData} 
                            />
                        ) : (
                            <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
                                <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
                                <div style={{ fontSize: 16, marginBottom: 8 }}>No active venues</div>
                                <div style={{ fontSize: 14 }}>
                                    Use the "Add Venue" button to invite venues to your roster.
                                </div>
                            </div>
                        )}
                        {hasSearch ? (
                            this.props.agentVenueStore.isMoreAgentVenues && (
                                <div style={{ textAlign: "center", padding: "16px 0" }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={this.handleShowMore}
                                        disabled={isLoadingVenues}
                                        style={{ color: "#FF0C9C", borderColor: "#FF0C9C" }}
                                    >
                                        Load More Results
                                    </Button>
                                </div>
                            )
                        ) : (
                            <VenuePaginationToolbar 
                                onLoadPage={this.handleLoadPage}
                                onReset={this.handleResetToPaginated}
                            />
                        )}
                    </div>
                </Paper>

                <EditAgentModal
                    open={this.state.editBookingDaysOpen}
                    onClose={this.closeEditBookingDays}
                    venueContext={true}
                />

                <VenueActModal
                    open={this.state.venueActModalState}
                    onClose={this.closeActModal}
                    openAddActModal={this.openAddActModal}
                    venue={this.state.agentVenue ? this.state.agentVenue.venue : undefined}
                    manage={this.state.agentVenue ? this.state.agentVenue.manage : false}
                />

                <AgentActInvitationModal
                    open={this.state.actInvitationModalState}
                    onClose={this.closeAddActModal}
                    venue={this.state.agentVenue ? this.state.agentVenue.venue : undefined}
                />

                <ConfirmationModal
                    open={this.state.deleteModalState}
                    title={"Remove Venue"}
                    message={`<p>Are you sure you want to remove <b>${this.state.venueName}</b>? Your current bookings at <b>${this.state.venueName}</b> will be cancelled.</p>`}
                    closeHandler={this.closeDeleteModal}
                    confirmationHandler={this.handleDelete}
                    confirmationLabel="Remove"
                />

                <AgentVenueContactModal
                    open={this.state.openAgentVenueContactModal}
                    agentVenue={this.state.agentVenueContact}
                    onClose={this.closeAgentVenueModal}
                    onSave={this.handleVenueContactSave}
                />

                <AgentVenueUpdateIncludeSuper
                    open={this.state.openUpdateAgentVenueIncludeSuper}
                    onClose={this.handleCloseAgentVenueIncludeSuperDefault}
                    data={this.state.agentVenue}
                    onSave={this.handleSaveAgentVenueIncludeSuperDefault}
                />
            </Fragment>
        );
    }
}

export default AgentVenueTable;
